import FileSaver from 'file-saver';
import history from '../shared/history';
import { getUserManager } from '../shared/userManager';
import { parseContentDispositionFilename } from './parseContentDispositionFilename';
import userStore from './UserStore';
import { assign } from './AssignForApi';
import ImpersonationStore from './ImpersonationStore';
import { stripTranslationKeyMarkers } from './translationKeyMarkers';

interface IApi {
    get(url: string): any;

    post(url: string, data: any): any;
}

interface IApiOptions {
    redirectOnAccessErrors?: boolean;
}

interface IRetryOptions {
    numberOfRetries: number;
    delay?: number;
}

enum ResponseErrorType {
    Unknown = 0,
    NotAssistantForOrder = 1,
    Forbidden = 2,
    InternalServerError = 3,
    NotPlacedByThisBroker = 4
}

let handle401Promise: Promise<any>;

export function isErrorDueToUnauthorizedRequest(message: unknown): boolean {
    if (
        typeof message === 'object' &&
        // @ts-ignore
        message.message === 'Authorization has been denied for this request.'
    ) {
        return true;
    }

    return false;
}

export function isAbortError(error: unknown): boolean {
    return typeof error === 'object' && error !== null && (error as Error).name === 'AbortError';
}

export function manageAuthorization(file: any) {
    let userManager = getUserManager();
    userManager.signinSilent().then(() => {
        this.options.headers = new Api().appendHeaders();
        this.processFile(file);
    });
}

class Api implements IApi {
    private serviceBase: string;
    private redirectOnAccessErrors: boolean;

    constructor(options: IApiOptions = {}) {
        const { apiUrl } = window.appConfig;
        this.serviceBase = apiUrl;
        this.redirectOnAccessErrors = options.redirectOnAccessErrors !== false;
    }

    public get(
        url: string,
        q: any = {},
        retryOptions?: IRetryOptions,
        signal?: AbortSignal
    ): Promise<any> {
        let queryString = Object.keys(q)
            .filter((x) => q[x])
            .map((k: any) => {
                if (q[k] instanceof Array) {
                    let param = '';
                    let array: any[] = q[k] as any[];
                    for (let i = 0; i < array.length; i++) {
                        param += k + '=' + encodeURI(stripTranslationKeyMarkers(String(array[i])));
                        if (i < array.length - 1) {
                            param += '&';
                        }
                    }
                    return param;
                }
                return k + '=' + stripTranslationKeyMarkers(String(q[k]));
            })
            .join('&');

        let request = () =>
            fetch(this.serviceBase + url + (queryString ? '?' + queryString : ''), {
                method: 'GET',
                headers: this.appendHeaders(),
                signal
            });

        return this.executeRequest(request, retryOptions);
    }

    public downloadFile(url: string, data?: any) {
        return this.get(url, data).then((response) => {
            return this.saveFile(response);
        });
    }

    public async downloadDNGFile(url: string) {
        let response = await fetch(this.serviceBase + url, {
            method: 'GET',
            headers: this.appendHeaders({
                'Content-Type': 'image/x-adobe-dng'
            })
        });
        return this.saveFile(response);
    }

    public async downloadCSVFile(url: string): Promise<any> {
        let request = () =>
            fetch(this.serviceBase + url, {
                method: 'GET',
                headers: this.appendHeaders({
                    'Content-Type': 'text/csv'
                })
            });

        await this.handleRequest(request)
            .then(this.checkStatus)
            .then(this.parseResponse)
            .then(async (response) => {
                if (
                    response &&
                    response.headers &&
                    response.headers.get('content-disposition') !== null
                ) {
                    this.saveFile(response);
                }
                return response;
            })
            .catch((error) => {
                console.error(error);
                if (error.Message && error.Message.indexOf('Unauthorized') != -1) {
                    //Do nothing
                } else {
                    // eslint-disable-next-line
                    console.error('executeRequest error', error);
                }

                throw error;
            });
    }

    public downloadFilePost(url: string, data: any) {
        return this.post(url, data).then((response) => {
            if (response) {
                this.saveFile(response);
            }

            return response;
        });
    }

    public post(url: string, data: any, retryOptions?: IRetryOptions): Promise<any> {
        let request = () =>
            fetch(this.serviceBase + url, {
                method: 'POST',
                body: stripTranslationKeyMarkers(JSON.stringify(assign({}, data))),
                headers: this.appendHeaders({
                    'Content-Type': 'application/json'
                })
            });

        return this.executeRequest(request, retryOptions);
    }

    public postFiles(url: string, data: any[]): Promise<any> {
        let formData = new FormData();
        data.map((element, index) => {
            formData.append(`file[${index}]`, element, element.name);
        });

        let request = () =>
            fetch(this.serviceBase + url, {
                method: 'POST',
                body: formData,
                headers: this.appendHeaders()
            });

        return this.executeRequest(request);
    }

    public put(url: string, data: any = {}): Promise<any> {
        let request = () =>
            fetch(this.serviceBase + url, {
                method: 'PUT',
                body: stripTranslationKeyMarkers(JSON.stringify(assign({}, data))),
                headers: this.appendHeaders({
                    'Content-Type': 'application/json'
                })
            });

        return this.executeRequest(request);
    }

    public delete(url: string): Promise<any> {
        let request = () =>
            fetch(this.serviceBase + url, {
                method: 'DELETE',
                headers: this.appendHeaders()
            });

        return this.executeRequest(request);
    }

    executeRequest(requestFunc: () => Promise<Response>, settings?: IRetryOptions) {
        return this.handleRequest(requestFunc, settings)
            .then(this.checkStatus)
            .then(this.parseResponse)
            .catch((error) => {
                if (isAbortError(error)) {
                    throw error;
                }

                console.error(error);
                if (error.Message && error.Message.indexOf('Unauthorized') != -1) {
                    //Do nothing
                } else {
                    // eslint-disable-next-line
                    console.error('executeRequest error', error);
                }

                throw error;
            });
    }

    appendHeaders(headers: Record<string, string> = {}): Record<string, string> {
        headers['pragma'] = 'no-cache';
        headers['cache-control'] = 'no-cache';
        const { site } = window.appConfig;
        headers['calling-site'] = site;
        return this.addAuthorizationHeader(headers);
    }

    public applyHeadersToXhr(xhr: XMLHttpRequest): void {
        let headers = this.appendHeaders();
        Object.keys(headers).forEach((name) => xhr.setRequestHeader(name, headers[name]));
    }

    addAuthorizationHeader(headers: Record<string, string> = {}): Record<string, string> {
        let authHeader = this.calculateAuthHeader();
        if (authHeader) {
            authHeader.forEach((x) => (headers[x.name] = x.value));
        }
        return headers;
    }

    public getServiceBase() {
        return this.serviceBase;
    }

    public calculateAuthHeader(): Array<{ name: string; value: string }> {
        let { user, impersonatedCountry, accessToken } = this.getAuthData();
        let impersonatedUserId = ImpersonationStore.getUserImpersonationId();

        let headers: Array<{ name: string; value: string }> = [];
        if (accessToken) {
            headers.push({
                name: 'Authorization',
                value: `Bearer ${accessToken}`
            });
        }
        if (user && ImpersonationStore.isImpersonated()) {
            headers.push({ name: 'Impersonate', value: impersonatedUserId! });
        } else if (impersonatedCountry) {
            headers.push({
                name: 'ImpersonateCountry',
                value: impersonatedCountry.id.toString()
            });
        }
        return headers;
    }

    public getAuthData() {
        let user = userStore.fetchUserFromLocalStorage();
        let impersonatedCountry = userStore.fetchCountryImpersonationFromSessionStorage();
        let accessToken = user && user.access_token ? user.access_token : undefined;
        return { user, impersonatedCountry, accessToken };
    }

    checkStatus(response: Response) {
        if (response.status >= 200 && response.status < 300) {
            return response;
        } else {
            let errorMessage: string = response.statusText;
            let error = new Error(errorMessage) as any;
            error.response = response;
            throw error;
        }
    }

    parseResponse(response: Response): any {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
            return response.json();
        } else if (contentType && contentType.indexOf('application/zip') !== -1) {
            return response;
        } else if (contentType && contentType.indexOf('image/jpeg') !== -1) {
            return response;
        } else if (contentType && contentType.indexOf('image/png') !== -1) {
            return response;
        } else if (contentType && contentType.indexOf('application/pdf') !== -1) {
            return response;
        } else if (
            contentType &&
            contentType.indexOf(
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ) !== -1
        ) {
            return response;
        } else if (contentType && contentType.indexOf('application/octet') !== -1) {
            return response;
        } else if (contentType && contentType.indexOf('text/csv') !== -1) {
            return response;
        } else if (contentType && contentType.indexOf('text/calendar') !== -1) {
            return response;
        }
    }

    readFileValueChunks(reader, returnValue, index): Promise<any> {
        return reader.read().then((chunk): any => {
            if (chunk.done) {
                return returnValue;
            } else {
                let lastIndex = 0;
                for (let i = 0; i < chunk.value.length; i++) {
                    returnValue[index + i] = chunk.value[i];
                    lastIndex = index + i;
                }
                return this.readFileValueChunks(reader, returnValue, lastIndex + 1);
            }
        });
    }

    handleResponse(requestFunc: () => Promise<Response>) {
        return requestFunc().then((response) => {
            if (response.status === 401) {
                if (!handle401Promise) {
                    let userManager = getUserManager();

                    handle401Promise = userManager
                        .signinSilent()
                        .then(() => {
                            handle401Promise = null;
                        })
                        .catch((error) => {
                            console.error('Authentication error', error);

                            userManager.clearStaleState().catch((e) => {
                                console.error('Error clearing stale state', e);
                            });

                            userManager
                                .removeUser()
                                .then(() => {
                                    userStore.removeUserFromStorage();
                                    handle401Promise = null;
                                    window.location.reload();
                                })
                                .catch((e) => {
                                    userStore.removeUserFromStorage();
                                    handle401Promise = null;
                                    console.error('Error removing user', e);
                                });
                        });
                }
                return handle401Promise.then(() => requestFunc());
            } else if (response.status === 404 && this.redirectOnAccessErrors) {
                history.replace('/404');
                return response;
            } else if (response.status === 403 && this.redirectOnAccessErrors) {
                response.json().then((data) => {
                    if (data && data.error.type == ResponseErrorType.NotPlacedByThisBroker) {
                        history.replace('/no-access-brokersystem', {
                            brokerSystemEmail: data.error.additionalData.brokerSystemEmail,
                            sE360Email: data.error.additionalData.sE360Email,
                            brokerSystem: data.error.additionalData.brokerSystem
                        });
                    } else {
                        history.replace('/no-access');
                    }
                });

                return response;
            } else {
                return response;
            }
        });
    }

    private handleRequest(
        fetchRequest: () => Promise<Response>,
        settings?: IRetryOptions
    ): Promise<Response> {
        let limit = settings ? settings.numberOfRetries : 0;
        return new Promise((resolve, reject) => {
            let retryFetch = (numberOfTries) => {
                this.handleResponse(fetchRequest)
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        if (numberOfTries > 0) {
                            retry(numberOfTries);
                        } else {
                            reject(error);
                        }
                    });
            };

            let retry = (numberOfTries) => {
                setTimeout(
                    () => retryFetch(--numberOfTries),
                    settings.delay ? settings.delay : 10000
                );
            };

            retryFetch(limit);
        });
    }

    private saveFile(response: any) {
        return response.blob().then((file) => {
            let blob = new Blob([file], {
                type: response.headers.get('content-type')
            });
            FileSaver.saveAs(
                blob,
                parseContentDispositionFilename(response.headers.get('content-disposition'))
            );
        });
    }
}

export default Api;
